import { deepClone } from '@src/utility/deepClone';
import { v4 as uuid } from 'uuid';

import { LayoutSegment } from './LayoutSegment';

import type { AnchorType, IScene } from './types';

/**
 * Scene class
 * Represents a scene on the timeline with anchored start/end points
 * and an associated layout segment that defines track arrangements
 */
export class Scene implements IScene {
  private _id: string;
  private _anchorStart: AnchorType;
  private _anchorEnd: AnchorType;
  private _layout: LayoutSegment;

  constructor(
    props: Partial<Omit<IScene, 'layout'>> & {
      anchorStart: AnchorType;
      anchorEnd: AnchorType;
      layout: LayoutSegment;
    } & { id?: string },
  ) {
    this._id = props.id ?? uuid();
    this._anchorStart = props.anchorStart;
    this._anchorEnd = props.anchorEnd;
    this._layout = props.layout;
  }

  // Getters
  get id(): string {
    return this._id;
  }

  get anchorStart(): AnchorType {
    return this._anchorStart;
  }

  get anchorEnd(): AnchorType {
    return this._anchorEnd;
  }

  get layout(): LayoutSegment {
    return this._layout;
  }

  // Setters
  setAnchorStart(anchor: AnchorType): void {
    this._anchorStart = anchor;
  }

  setAnchorEnd(anchor: AnchorType): void {
    this._anchorEnd = anchor;
  }

  setLayout(layout: LayoutSegment): void {
    this._layout = layout;
  }

  /**
   * Serializes to a plain JSON-safe object.
   * Anchors are deep-cloned so callers (e.g. structuredClone)
   * never receive MobX proxy references.
   */
  toJSON(): IScene {
    return {
      id: this._id,
      anchorStart: deepClone(this._anchorStart),
      anchorEnd: deepClone(this._anchorEnd),
      layout: this._layout.toJSON(),
    };
  }

  /**
   * Creates a Scene from JSON
   */
  static fromJSON(json: IScene): Scene {
    return new Scene({
      id: json.id,
      anchorStart: json.anchorStart,
      anchorEnd: json.anchorEnd,
      layout: LayoutSegment.fromJSON(json.layout),
    });
  }
}
